import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ConsoleShell from '../../components/admin2/ConsoleShell';
import { fetchToday } from '../../lib/consoleToday';
import TranslationHealthPanel from './TranslationHealthPanel';

// ── "Today" ────────────────────────────────────────────────────────────────
//
// This page used to be six counters and four shortcuts. A counter tells you a
// number and leaves you to go find the thing. An operations console is opened
// the other way round: you already know there is work, you want the next item.
//
// So: two signup cards, then ONE list of everything waiting on a person, oldest
// first, each row carrying the verb that clears it. A stream that grows does not
// become a new sidebar entry — it becomes more rows here.
//
// What is deliberately NOT on this page: anything that is merely in progress. A
// managed request already in sourcing, a supplier already verified, a request
// already sent to factories — all real work, none of it waiting on anyone here,
// and any of it would make the list un-clearable. A queue you cannot empty is a
// queue nobody reads.

const relTime = (iso, isAr) => {
  if (!iso) return '—';
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 60) return isAr ? `${Math.max(mins, 0)} دقيقة` : `${Math.max(mins, 0)}m`;
  const h = Math.floor(mins / 60);
  if (h < 24) return isAr ? `${h} ساعة` : `${h}h`;
  const d = Math.floor(h / 24);
  if (d < 30) return isAr ? (d === 1 ? 'يوم' : `${d} يوم`) : `${d}d`;
  const m = Math.floor(d / 30);
  return isAr ? `${m} شهر` : `${m}mo`;
};

// Anything waiting longer than this is late by definition of the queue, not by
// anyone's judgement — it is the point at which "we will get to it" stopped
// being true. Three days: long enough that a weekend does not trip it.
const LATE_DAYS = 3;
const isLate = (iso) => !!iso && (Date.now() - new Date(iso).getTime()) > LATE_DAYS * 86400000;

const SEV_CLS = { review: 'warn', chase: 'danger', new: 'info' };

export default function ConsoleDashboard({ user, profile, lang }) {
  const isAr = lang === 'ar';
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    let alive = true;
    fetchToday({ isAr })
      .then((d) => { if (alive) setData(d); })
      .catch((e) => {
        if (!alive) return;
        console.error('[ConsoleDashboard] fetchToday failed:', e?.message || e);
        setErr(e?.message || 'load failed');
        setData({ items: [], counts: {}, signups: null });
      });
    return () => { alive = false; };
  }, [isAr]);

  const name = (profile?.full_name || '').split(' ')[0] || '';
  const items = useMemo(
    () => (data ? data.items.filter((i) => filter === 'all' || i.stream === filter) : []),
    [data, filter],
  );

  const today = new Date().toLocaleDateString(isAr ? 'ar-SA' : 'en-GB',
    { weekday: 'long', day: 'numeric', month: 'long' });

  const SIGNUPS = data?.signups ? [
    {
      key: 'traders',
      lbl: isAr ? 'التجّار المسجّلون' : 'Registered traders',
      d: data.signups.traders,
      go: '/admin2/traders',
    },
    {
      key: 'suppliers',
      lbl: isAr ? 'الموردون المسجّلون' : 'Registered suppliers',
      d: data.signups.suppliers,
      go: '/admin2/suppliers?tab=registered',
    },
  ] : [];

  const TABS = [
    { k: 'all', ar: 'الكل', en: 'All' },
    { k: 'suppliers', ar: 'الموردون', en: 'Suppliers' },
    { k: 'requests', ar: 'الطلبات', en: 'Requests' },
  ];

  return (
    <ConsoleShell user={user} profile={profile} lang={lang} active="dashboard">
      <div className="ac-page" style={{ fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
        {/* Above the worklist on purpose: a request whose translation is
            blocked cannot be sent to suppliers or given a public link, so
            it gates the day's work rather than sitting beneath it. The
            panel renders nothing when there is nothing wrong. */}
        <TranslationHealthPanel isAr={isAr} />

        <div className="ac-page-head">
          <div>
            <p className="ac-sub" style={{ margin: 0 }}>{today}</p>
            <h1 className="ac-h1">{isAr ? `صباح الخير${name ? ' يا ' + name : ''}` : `Good morning${name ? ', ' + name : ''}`}</h1>
            <p className="ac-hello">
              {!data ? '…'
                : data.counts.all > 0
                  ? (isAr
                    ? <>عندك <b>{data.counts.all} إجراء</b> ينتظر متابعتك.</>
                    : <>You have <b>{data.counts.all} items</b> waiting on you.</>)
                  : (isAr ? 'لا شيء ينتظرك الآن 👌' : "Nothing is waiting on you 👌")}
            </p>
          </div>
        </div>

        {err && <p style={{ color: 'var(--ac-danger)', fontSize: 13 }}>{err}</p>}

        {/* Who signed up — traders on one side, suppliers on the other. */}
        {data?.signups && (
          <div className="ac-signups">
            {SIGNUPS.map((c) => (
              <button key={c.key} type="button" className="ac-signup" onClick={() => nav(c.go)}>
                <p className="ac-signup-lbl">{c.lbl}</p>
                <span className="ac-signup-num">
                  <b>{c.d.total}</b>
                  <span className={`ac-signup-delta${c.d.week ? '' : ' zero'}`}>
                    {isAr ? 'هذا الأسبوع' : 'This week'} {c.d.week ? `+${c.d.week}` : '—'}
                  </span>
                  <span className={`ac-signup-delta${c.d.today ? '' : ' zero'}`}>
                    {isAr ? 'اليوم' : 'Today'} {c.d.today ? `+${c.d.today}` : '—'}
                  </span>
                </span>
                <p className="ac-signup-foot">
                  {c.d.newest
                    ? `${isAr ? 'آخر تسجيل: ' : 'Latest: '}${c.d.newest.name || (isAr ? 'بلا اسم' : 'unnamed')} · ${relTime(c.d.newest.at, isAr)}`
                    : (isAr ? 'لا تسجيلات بعد' : 'No signups yet')}
                </p>
              </button>
            ))}
          </div>
        )}

        <div className="ac-page-head" style={{ marginBottom: 12 }}>
          <div>
            <h2 className="ac-h1" style={{ fontSize: 19 }}>{isAr ? 'ما يحتاج إجراء الآن' : 'Needs action now'}</h2>
            <p className="ac-sub">{isAr ? 'الأقدم انتظارًا أولًا' : 'Longest waiting first'}</p>
          </div>
        </div>

        <div className="ac-tabs">
          {TABS.map((t) => (
            <button key={t.k} type="button" className={`ac-tab${filter === t.k ? ' on' : ''}`} onClick={() => setFilter(t.k)}>
              {isAr ? t.ar : t.en}
              <span className="ac-tab-badge">{data ? (data.counts[t.k] ?? 0) : '—'}</span>
            </button>
          ))}
        </div>

        {!data ? (
          <div className="ac-worklist">{Array.from({ length: 4 }).map((_, i) => <div key={i} className="ac-skel" style={{ height: 76 }} />)}</div>
        ) : items.length === 0 ? (
          <div className="ac-empty">
            {data.counts.all === 0
              ? (isAr ? 'القائمة فاضية — لا شيء ينتظر أحدًا.' : 'The queue is empty — nothing is waiting on anyone.')
              : (isAr ? 'لا شيء في هذا التصنيف.' : 'Nothing in this filter.')}
          </div>
        ) : (
          <div className="ac-worklist">
            {items.map((it) => (
              <article className="ac-work" key={it.id}>
                <span className={`ac-work-accent ${it.severity}`} />
                <div className="ac-work-main">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}>
                    <span className={`ac-badge ${SEV_CLS[it.severity] || 'neutral'}`}><span className="dot" />{isAr ? it.tagAr : it.tagEn}</span>
                    {isLate(it.since) && (
                      <span className="ac-badge danger"><span className="dot" />{isAr ? `متأخر ${relTime(it.since, isAr)}` : `Late · ${relTime(it.since, isAr)}`}</span>
                    )}
                  </div>
                  <h3 className="ac-work-title">{it.title}</h3>
                  <p className="ac-work-sub">{[it.subtitle, it.note].filter(Boolean).join(' · ') || '—'}</p>
                </div>
                <div className="ac-work-when">
                  <small>{isAr ? it.sinceLabelAr : it.sinceLabelEn}</small>
                  <strong>{isAr ? `منذ ${relTime(it.since, isAr)}` : relTime(it.since, isAr)}</strong>
                </div>
                <button className="ac-btn ac-btn-primary ac-btn-sm" onClick={() => nav(it.href)}>
                  {isAr ? it.actionAr : it.actionEn}
                </button>
              </article>
            ))}

            {/* The rest of the stalled backlog, named rather than hidden. A count
                with a way in is honest; silently truncating a work queue is not. */}
            {data.chaseOverflow > 0 && filter !== 'requests' && (
              <button type="button" className="ac-empty" style={{ cursor: 'pointer', font: 'inherit', width: '100%' }}
                onClick={() => nav('/admin2/suppliers?tab=registered')}>
                {isAr
                  ? `و${data.chaseOverflow} مورّدًا آخر لم يكمل ملفه — افتح القائمة`
                  : `And ${data.chaseOverflow} more suppliers with an incomplete profile — open the list`}
              </button>
            )}
          </div>
        )}
      </div>
    </ConsoleShell>
  );
}
